/**
 * Runtime hook for fetching component rules from the hosting module's API.
 *
 * Powers the {@link withComponentRules} HOC — it returns the `active`
 * rules for a given component id in the camelCased {@link ComponentRule}
 * shape the evaluator expects.
 *
 * **Batched per-module fetch.** With register-by-default, a single page
 * can mount ~17 registered components. Fetching active rules
 * per-component would fan out into ~17 near-identical requests. Instead
 * we fetch **once per module** (`<module>` = the substring before the
 * first `.` in `<module>.<name>`): the module query asks the backend for
 * *all* active rules whose `component_id` starts with `"<module>."` in a
 * single request, and each `useComponentRules(componentId)` reads that
 * one cached query and `select`s only its own slice. React Query dedupes
 * concurrent identical module queries automatically, so 17 components of
 * the same module mounting together trigger exactly **one** network
 * request.
 *
 * The hook is deliberately **not** reusing the admin-side
 * `componentRulesService` / `adminApiClient` pair:
 *
 * - `adminApiClient` reads its auth token from
 *   `localStorage.admin_auth_token`, which a claimflow-logged-in user
 *   does not have. Using it from a claimflow page would 401 on every
 *   request, triggering the admin 401 interceptor and an infinite
 *   reload loop (see commit `de78520` for the fix that split the two
 *   pipelines).
 *
 * - The DTO → domain mapping is trivial; we inline a tiny local helper
 *   instead of pulling in the admin module as a runtime dependency.
 *
 * **Module-aware client/path.** The request goes through the client and
 * route group of the hosting module, read from the nearest
 * `IntegratedComponentProvider` (`/<module>/component-rules` with that
 * module's authenticated client) when the component belongs to that
 * module; otherwise (no provider, or a component of another module — e.g.
 * an admin preview) it falls back to the historical claimflow client/path.
 * See `resolveRulesApiTarget` (`./rulesApiTarget.ts`). Claimflow pages
 * resolve to the exact same request as before.
 *
 * Fail-open: on any fetch error we return `rules: []` so the wrapped
 * component keeps rendering with its original props. A surfaced `error`
 * is still returned for debugging.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  componentRulesPath,
  moduleOf,
  useRulesApiTarget,
  type RulesApiTarget,
} from './rulesApiTarget';
import type {
  ComponentRule,
  ConditionGroup,
  RuleEffect,
  RuleStatus,
} from './types';

// ---------------------------------------------------------------------------
// Local DTO types — identical to the admin-side DTO but copied so we do
// not import from `@/modules/admin/*` at claimflow runtime.
// ---------------------------------------------------------------------------

/**
 * Shape of a single component-rule DTO as returned by the Origami API.
 *
 * Matches the admin-side `ComponentRuleDTO` from
 * `src/modules/admin/api/componentRulesService.ts` — duplicated here so
 * the claimflow runtime does not depend on the admin module.
 */
interface ComponentRuleDto {
  id: string;
  tenant_id: string | null;
  component_id: string;
  name: string;
  description: string | null;
  nl_input: string | null;
  rule_json: {
    conditions: ConditionGroup;
    effects: RuleEffect[];
  };
  status: RuleStatus;
  priority: number;
  created_by: number | null;
  approved_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Shape of the `GET /<module>/component-rules` collection response.
 */
interface ComponentRuleCollectionResponse {
  data: ComponentRuleDto[];
}

// ---------------------------------------------------------------------------
// Local DTO → domain mapper
// ---------------------------------------------------------------------------

/**
 * Convert a snake-cased DTO into the camelCased {@link ComponentRule}
 * shape the evaluator expects.
 *
 * Kept as a **local** helper so the runtime hook does not need to import
 * `mapComponentRuleFromDTO` from the admin service layer. The two
 * mappers share the same trivial structure and can diverge safely if the
 * admin service ever adds admin-specific fields.
 */
function mapRuleFromDto(dto: ComponentRuleDto): ComponentRule {
  return {
    id: dto.id,
    componentId: dto.component_id,
    name: dto.name,
    description: dto.description ?? '',
    nlInput: dto.nl_input ?? '',
    conditions: dto.rule_json.conditions,
    effects: dto.rule_json.effects,
    status: dto.status,
    priority: dto.priority,
  };
}

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

/**
 * Stale-time for runtime rule queries.
 *
 * One minute — long enough to avoid thrashing the backend on rapid
 * re-renders, short enough that newly-activated rules show up without
 * a manual refresh.
 */
const RUNTIME_RULES_STALE_TIME_MS = 60_000;

/**
 * Shape returned by {@link useComponentRules}.
 *
 * The HOC only needs the minimal subset: the array of mapped rules, a
 * loading flag so it can render with original props on the first paint,
 * and an error value for diagnostics. The raw `UseQueryResult` is
 * deliberately not re-exported so the HOC cannot accidentally depend on
 * React Query internals.
 */
export interface UseComponentRulesResult {
  /** Active component rules for the given component id. */
  rules: ComponentRule[];
  /** `true` while the initial fetch is in flight. */
  isLoading: boolean;
  /** Any error returned by the fetch — `null` / `undefined` on success. */
  error: unknown;
}

/**
 * React Query key of a module's active runtime rules.
 *
 * `['configurable', 'component-rules', 'runtime', 'module', module]` when
 * the rules are served by the component's own module (claimflow pages,
 * extranet pages — the historical key). When they are served through
 * another module's route group (the claimflow fallback, e.g. an admin
 * preview of an `extranet.*` card) the serving module is appended, so the
 * two sources never share a cache entry.
 */
export function moduleRulesQueryKey(
  module: string,
  target: RulesApiTarget,
): readonly unknown[] {
  const base = ['configurable', 'component-rules', 'runtime', 'module', module];
  return target.module === module ? base : [...base, 'via', target.module];
}

/**
 * Internal: fetch (and cache) the active runtime rules for an entire
 * module in a single request.
 *
 * Cache key: {@link moduleRulesQueryKey}. The client and route group come
 * from the nearest `IntegratedComponentProvider` when it hosts `module`,
 * else from the claimflow fallback.
 *
 * One query per module per page — React Query dedupes the concurrent
 * identical queries that the N mounted components issue, so the network
 * sees exactly one request. The returned array is sliced per component
 * id by {@link useComponentRules} via React Query's `select`.
 *
 * Fail-open: any axios error resolves to `[]` so wrapped components keep
 * rendering with their original props.
 */
function useModuleComponentRules(
  module: string,
  select: (rules: ComponentRule[]) => ComponentRule[],
): UseQueryResult<ComponentRule[], unknown> {
  const target = useRulesApiTarget(module);
  return useQuery<ComponentRule[], unknown, ComponentRule[]>({
    queryKey: moduleRulesQueryKey(module, target),
    queryFn: async () => {
      const response =
        await target.apiClient.get<ComponentRuleCollectionResponse>(
          componentRulesPath(target),
          {
            params: {
              module,
              status: 'active',
            },
          },
        );
      const items = response.data.data;
      if (!Array.isArray(items)) return [];
      return items.map(mapRuleFromDto);
    },
    select,
    staleTime: RUNTIME_RULES_STALE_TIME_MS,
    enabled: module !== '',
  });
}

/**
 * Fetch the active runtime rules for a registered component.
 *
 * Used only by the {@link withComponentRules} HOC at module render time
 * (claimflow, extranet, …) — the admin module has its own sibling hooks
 * that use `adminApiClient`.
 *
 * Internally backed by a **per-module** React Query
 * ({@link useModuleComponentRules}) and sliced down to this component's
 * rules with `select`, so many components of the same module share a
 * single underlying fetch. The public signature and return shape are
 * unchanged from the previous per-component implementation, so callers
 * (the HOC) are unaffected.
 *
 * Fail-open: any axios error resolves to `rules: []` so the wrapped
 * component continues to render with its original props.
 */
export function useComponentRules(
  componentId: string,
): UseComponentRulesResult {
  const module = moduleOf(componentId);

  // Slice the module-wide list down to this component's rules. `select`
  // runs against the shared cached data so every component reads the same
  // single fetch and only re-renders when its own slice changes.
  const query = useModuleComponentRules(module, (rules) =>
    rules.filter((rule) => rule.componentId === componentId),
  );

  return {
    rules: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
